/**
 * Module: web/src/composables/useLockout.ts
 * Author: Salvo Praxis (w/ AI pair programming partner Claude Code by Anthropic)
 * Version: 1.0.0
 * Date: 2026-07-08
 *
 * 423 {code:'locked', unlock_at} lockout countdown (doc 04 sect 6.1/6.5) --
 * shared by the login + PIN views. Ticks a mm:ss remaining string until
 * unlock_at passes, then clears itself.
 */
import { computed, onUnmounted, ref } from 'vue';

export function useLockout() {
  const unlockAt = ref<Date | null>(null);
  const now = ref(Date.now());
  let timer: ReturnType<typeof setInterval> | null = null;

  const lockedOut = computed(() => unlockAt.value !== null && unlockAt.value.getTime() > now.value);

  const remainingLabel = computed(() => {
    if (!unlockAt.value) return '';
    const ms = unlockAt.value.getTime() - now.value;
    if (ms <= 0) return '';
    const totalSec = Math.ceil(ms / 1000);
    const m = Math.floor(totalSec / 60);
    const s = totalSec % 60;
    return `${m}:${s.toString().padStart(2, '0')}`;
  });

  function startLockout(unlockAtIso: unknown) {
    const parsed = typeof unlockAtIso === 'string' ? new Date(unlockAtIso) : null;
    // If the server didn't give a usable timestamp, show a generic 15-min window.
    unlockAt.value = parsed && !Number.isNaN(parsed.getTime())
      ? parsed
      : new Date(Date.now() + 15 * 60_000);
    stopTimer();
    timer = setInterval(() => {
      now.value = Date.now();
      if (unlockAt.value && unlockAt.value.getTime() <= now.value) {
        unlockAt.value = null;
        stopTimer();
      }
    }, 1000);
  }

  function stopTimer() {
    if (timer !== null) {
      clearInterval(timer);
      timer = null;
    }
  }

  onUnmounted(stopTimer);

  return { lockedOut, remainingLabel, startLockout };
}
